import {
  builtinBrandDrafts,
  ClientConfigDraft,
  cloneClientConfigDraft,
  normalizeBrandSlug,
} from "./clientConfig";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, "");
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const SUPABASE_BRANDS_TABLE =
  process.env.REACT_APP_SUPABASE_BRANDS_TABLE || "showcase_brands";

export type BrandRecord = {
  id?: number | string;
  created_at?: string;
  slug: string;
  display_name?: string;
  brand_data?: ClientConfigDraft | null;
};

export const hasSupabaseBrandConfig = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

const getHeaders = () => ({
  apikey: SUPABASE_ANON_KEY as string,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

const normalizeRecord = (record: BrandRecord): BrandRecord => {
  const draft = cloneClientConfigDraft(record.brand_data || {});
  const normalizedSlug =
    normalizeBrandSlug(record.slug || draft.id || record.display_name || "") || "brand";

  return {
    ...record,
    slug: normalizedSlug,
    display_name: record.display_name || draft.displayName || normalizedSlug,
    brand_data: {
      ...draft,
      id: normalizedSlug,
      displayName: draft.displayName || record.display_name || normalizedSlug,
    },
  };
};

const sortBySlug = (items: BrandRecord[]) =>
  [...items].sort((a, b) => a.slug.localeCompare(b.slug, "ru"));

export const getBuiltinBrandRecords = (): BrandRecord[] =>
  sortBySlug(
    Object.entries(builtinBrandDrafts).map(([slug, draft]) =>
      normalizeRecord({
        id: `builtin:${slug}`,
        slug,
        display_name: draft.displayName || slug,
        brand_data: cloneClientConfigDraft(draft),
      }),
    ),
  );

export const fetchBrands = async (): Promise<BrandRecord[]> => {
  const builtin = getBuiltinBrandRecords();

  if (!hasSupabaseBrandConfig) {
    return builtin;
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${SUPABASE_BRANDS_TABLE}?select=id,created_at,slug,display_name,brand_data&order=created_at.asc`,
    {
      headers: getHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error("Не удалось загрузить бренды из Supabase.");
  }

  const remote = ((await response.json()) as BrandRecord[]).map(normalizeRecord);
  const merged = new Map<string, BrandRecord>();

  builtin.forEach((record) => merged.set(record.slug, record));
  remote.forEach((record) => merged.set(record.slug, record));

  return sortBySlug(Array.from(merged.values()));
};

export const fetchBrandBySlug = async (slug?: string): Promise<BrandRecord | null> => {
  const normalizedSlug = normalizeBrandSlug(slug);

  if (!normalizedSlug) {
    return null;
  }

  if (!hasSupabaseBrandConfig) {
    const builtin = builtinBrandDrafts[normalizedSlug];
    return builtin
      ? normalizeRecord({
          id: `builtin:${normalizedSlug}`,
          slug: normalizedSlug,
          display_name: builtin.displayName || normalizedSlug,
          brand_data: cloneClientConfigDraft(builtin),
        })
      : null;
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${SUPABASE_BRANDS_TABLE}?slug=eq.${encodeURIComponent(
      normalizedSlug,
    )}&select=id,created_at,slug,display_name,brand_data&limit=1`,
    {
      headers: getHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error("Не удалось загрузить бренд.");
  }

  const payload = (await response.json()) as BrandRecord[];
  const remoteRecord = payload[0];

  if (remoteRecord) {
    return normalizeRecord(remoteRecord);
  }

  const builtin = builtinBrandDrafts[normalizedSlug];

  return builtin
    ? normalizeRecord({
        id: `builtin:${normalizedSlug}`,
        slug: normalizedSlug,
        display_name: builtin.displayName || normalizedSlug,
        brand_data: cloneClientConfigDraft(builtin),
      })
    : null;
};

export const saveBrand = async (record: BrandRecord): Promise<BrandRecord> => {
  if (!hasSupabaseBrandConfig) {
    throw new Error(
      "Добавьте REACT_APP_SUPABASE_URL и REACT_APP_SUPABASE_ANON_KEY, чтобы сохранять бренды.",
    );
  }

  const normalizedSlug = normalizeBrandSlug(record.slug);

  if (!normalizedSlug) {
    throw new Error("Укажите slug бренда.");
  }

  const payload = {
    slug: normalizedSlug,
    display_name:
      record.display_name || record.brand_data?.displayName || normalizedSlug,
    brand_data: {
      ...(record.brand_data || {}),
      id: normalizedSlug,
      displayName:
        record.brand_data?.displayName ||
        record.display_name ||
        normalizedSlug,
    },
  };

  const isUpdate =
    record.id != null && !String(record.id).startsWith("builtin:");
  const targetUrl = isUpdate
    ? `${SUPABASE_URL}/rest/v1/${SUPABASE_BRANDS_TABLE}?id=eq.${record.id}`
    : `${SUPABASE_URL}/rest/v1/${SUPABASE_BRANDS_TABLE}`;

  const response = await fetch(targetUrl, {
    method: isUpdate ? "PATCH" : "POST",
    headers: {
      ...getHeaders(),
      Prefer: "return=representation",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      isUpdate ? "Не удалось сохранить изменения бренда." : "Не удалось создать бренд.",
    );
  }

  const saved = (await response.json()) as BrandRecord[];
  const savedRecord = saved[0];

  if (!savedRecord) {
    throw new Error("Supabase не вернул сохранённую запись бренда.");
  }

  return normalizeRecord(savedRecord);
};

export const deleteBrand = async (record: BrandRecord) => {
  if (!hasSupabaseBrandConfig) {
    throw new Error(
      "Добавьте REACT_APP_SUPABASE_URL и REACT_APP_SUPABASE_ANON_KEY, чтобы удалять бренды.",
    );
  }

  if (record.id == null || String(record.id).startsWith("builtin:")) {
    throw new Error("Встроенные бренды нельзя удалить из Supabase.");
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${SUPABASE_BRANDS_TABLE}?id=eq.${record.id}`,
    {
      method: "DELETE",
      headers: getHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error("Не удалось удалить бренд.");
  }
};
